// Location Tracking Service for Real-Time ETA and Auto-SMS

class LocationTracker {
  constructor() {
    this.isTracking = false;
    this.watchId = null;
    this.trackingInterval = null;
    this.notifiedCustomers = new Set(); // Track who we've already notified
    this.onLocationUpdate = null;
    this.destinations = [];
    this.currentDestinationIndex = 0;
  }

  // Start tracking with destinations
  startTracking(destinations, onLocationUpdate, onETAUpdate) {
    if (this.isTracking) {
      console.warn('⚠️ Tracking already active');
      return;
    }

    console.log('🚗 Starting location tracking for', destinations.length, 'destinations');
    
    this.destinations = destinations;
    this.currentDestinationIndex = 0;
    this.notifiedCustomers.clear();
    this.onLocationUpdate = onLocationUpdate;
    this.onETAUpdate = onETAUpdate;
    this.isTracking = true;

    // Watch position continuously
    if (navigator.geolocation) {
      this.watchId = navigator.geolocation.watchPosition(
        (position) => {
          const coords = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          
          console.log('📍 Location update:', coords);
          
          if (this.onLocationUpdate) {
            this.onLocationUpdate(coords);
          }
          
          // Check ETA to next destination
          this.checkETAToNextDestination(coords);
        },
        (error) => {
          console.error('❌ Geolocation error:', error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 30000, // Cache for 30 seconds
        }
      );

      // Also poll every 30 seconds as backup
      this.trackingInterval = setInterval(() => {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const coords = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };
            
            if (this.onLocationUpdate) {
              this.onLocationUpdate(coords);
            }
            
            this.checkETAToNextDestination(coords);
          },
          (error) => {
            console.error('❌ Position poll error:', error);
          },
          {
            enableHighAccuracy: true,
            timeout: 5000,
            maximumAge: 0,
          }
        );
      }, 30000); // Poll every 30 seconds
    }
  }

  // Stop tracking
  stopTracking() {
    console.log('🛑 Stopping location tracking');
    
    this.isTracking = false;
    
    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }
    
    if (this.trackingInterval) {
      clearInterval(this.trackingInterval);
      this.trackingInterval = null;
    }
    
    this.notifiedCustomers.clear();
  }

  // Check ETA to next destination
  async checkETAToNextDestination(currentLocation) {
    if (!this.isTracking || this.currentDestinationIndex >= this.destinations.length) {
      return;
    }

    const destination = this.destinations[this.currentDestinationIndex];
    
    // Skip if already notified
    if (this.notifiedCustomers.has(destination.id)) {
      return;
    }

    try {
      // Use Google Maps Distance Matrix API for real-time ETA
      const service = new window.google.maps.DistanceMatrixService();
      
      const request = {
        origins: [currentLocation],
        destinations: [{ lat: destination.lat, lng: destination.lng }],
        travelMode: window.google.maps.TravelMode.DRIVING,
        drivingOptions: {
          departureTime: new Date(),
          trafficModel: window.google.maps.TrafficModel.BEST_GUESS,
        },
      };

      service.getDistanceMatrix(request, (response, status) => {
        if (status === 'OK') {
          const result = response.rows[0].elements[0];
          
          if (result.status === 'OK') {
            const durationInSeconds = result.duration_in_traffic
              ? result.duration_in_traffic.value
              : result.duration.value;
            
            const durationInMinutes = Math.round(durationInSeconds / 60);
            const distanceInMeters = result.distance.value;
            const distanceInMiles = (distanceInMeters / 1609.34).toFixed(1);
            
            console.log(`📊 ETA to ${destination.name}: ${durationInMinutes} min (${distanceInMiles} mi)`);
            
            // Notify callback with ETA update
            if (this.onETAUpdate) {
              this.onETAUpdate({
                destination,
                durationMinutes: durationInMinutes,
                distanceMiles: parseFloat(distanceInMiles),
                index: this.currentDestinationIndex,
              });
            }
            
            // Check if we should send notification (< 20 minutes)
            if (durationInMinutes <= 20) {
              console.log(`🔔 Triggering notification for ${destination.name} (${durationInMinutes} min away)`);
              this.triggerNotification(destination, durationInMinutes);
            }
          }
        } else {
          console.error('❌ Distance Matrix error:', status);
        }
      });
    } catch (error) {
      console.error('❌ Error checking ETA:', error);
    }
  }

  // Trigger SMS notification
  triggerNotification(destination, etaMinutes) {
    // Mark as notified to prevent duplicates
    this.notifiedCustomers.add(destination.id);
    
    console.log(`📤 Sending notification to ${destination.name} - ETA: ${etaMinutes} minutes`);
    
    // Call the notification callback (will be handled by the component)
    if (this.onNotificationTrigger) {
      this.onNotificationTrigger(destination, etaMinutes);
    }
  }

  // Set notification callback
  setNotificationCallback(callback) {
    this.onNotificationTrigger = callback;
  }

  // Move to next destination
  moveToNextDestination() {
    this.currentDestinationIndex++;
    console.log(`➡️ Moving to destination ${this.currentDestinationIndex + 1} of ${this.destinations.length}`);
    
    if (this.currentDestinationIndex >= this.destinations.length) {
      console.log('✅ All destinations reached!');
      this.stopTracking();
    }
  }

  // Get tracking status
  getStatus() {
    return {
      isTracking: this.isTracking,
      currentDestinationIndex: this.currentDestinationIndex,
      totalDestinations: this.destinations.length,
      notifiedCount: this.notifiedCustomers.size,
    };
  }
}

// Export singleton instance
export const locationTracker = new LocationTracker();

